import { useEffect, useState, useCallback } from "react";
import { WarningCircle, CheckCircle, ArrowsClockwise, Truck, Calendar } from "@phosphor-icons/react";
import { http } from "@/lib/api";

const SEV = {
  expired:  { bg: "#ef4444", label: "EXPIRED" },
  critical: { bg: "#f97316", label: "≤ 7 DAYS" },
  warning:  { bg: "#fbbf24", label: "≤ 14 DAYS" },
  soon:     { bg: "#4682b4", label: "≤ 30 DAYS" },
};

export default function ExpirationsPage() {
  const [data, setData] = useState({ rows: [], total: 0, expired: 0, critical: 0, warning: 0 });
  const [loading, setLoading] = useState(true);
  const [horizon, setHorizon] = useState(30);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData((await http.get(`/assets/expirations?days_ahead=${horizon}`)).data); }
    finally { setLoading(false); }
  }, [horizon]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="expirations-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#a1a1aa]">Fleet Compliance HQ</div>
          <h1 className="font-heading font-black text-3xl tracking-tight">Expirations</h1>
        </div>
        <div className="flex items-center gap-2">
          <select value={horizon} onChange={(e) => setHorizon(parseInt(e.target.value))}
            className="px-3 py-1.5 bg-[#1a1a1a] border border-[#2a2a2a] text-[10px] uppercase tracking-[0.25em] font-bold" data-testid="expirations-horizon">
            <option value={14}>Next 14 days</option>
            <option value={30}>Next 30 days</option>
            <option value={60}>Next 60 days</option>
            <option value={90}>Next 90 days</option>
          </select>
          <button onClick={load} disabled={loading} className="px-3 py-1.5 border border-[#2a2a2a] hover:border-[#ff8c00] text-[10px] uppercase tracking-[0.25em] font-bold flex items-center gap-2" data-testid="expirations-refresh">
            <ArrowsClockwise size={11} weight="bold" className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Tile label="Total alerts" value={data.total} accent="#a1a1aa" testid="exp-tile-total" />
        <Tile label="Expired" value={data.expired} accent="#ef4444" testid="exp-tile-expired" />
        <Tile label="≤ 7 days" value={data.critical} accent="#f97316" testid="exp-tile-critical" />
        <Tile label="≤ 14 days" value={data.warning} accent="#fbbf24" testid="exp-tile-warning" />
      </div>

      <div className="bg-[#0d0d0d] border border-[#2a2a2a]" data-testid="expirations-list">
        <div className="px-4 py-3 border-b border-[#2a2a2a] flex items-center justify-between">
          <h2 className="font-heading font-bold text-sm tracking-tight uppercase">Upcoming Expirations</h2>
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#71717a]">{data.rows.length} items</span>
        </div>
        {data.rows.length === 0 ? (
          <div className="px-4 py-14 text-center text-xs text-[#71717a] uppercase tracking-[0.25em]" data-testid="exp-empty">
            <CheckCircle size={28} weight="fill" className="text-[#22c55e] mx-auto mb-2" />
            All clear within {horizon} days. Add registration/insurance dates on each asset to enable alerts.
          </div>
        ) : (
          <div className="divide-y divide-[#2a2a2a]">
            {data.rows.map((r) => {
              const sev = SEV[r.severity] || SEV.soon;
              return (
                <div key={`${r.asset_id}-${r.field}`} className="px-4 py-3 flex items-center gap-3 flex-wrap" data-testid={`exp-row-${r.asset_id}-${r.field}`}>
                  <span className="text-[9px] uppercase tracking-[0.25em] font-black px-1.5 py-0.5 shrink-0" style={{ background: sev.bg, color: "#000" }}>
                    {sev.label}
                  </span>
                  <div className="w-7 h-7 bg-[#1a1a1a] border border-[#2a2a2a] flex items-center justify-center shrink-0">
                    <Truck size={11} className="text-[#ff8c00]" weight="bold" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap text-sm">
                      <span className="font-mono font-bold text-[#ff8c00]">{r.asset_name}</span>
                      {r.license_plate && <span className="text-[10px] text-[#71717a] font-mono">{r.license_plate}</span>}
                      <span className="text-white">· {r.kind}</span>
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717a] font-mono mt-0.5 flex items-center gap-1">
                      <Calendar size={9} weight="bold" /> {r.expires_on}
                      <span className="ml-2" style={{ color: sev.bg }}>
                        {r.days_left < 0 ? `${Math.abs(r.days_left)} d overdue` : `in ${r.days_left} d`}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function Tile({ label, value, accent, testid }) {
  return (
    <div className="bg-[#0d0d0d] border border-[#2a2a2a] p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.25em]" style={{ color: accent }}>{label}</div>
      <div className="text-2xl font-heading font-black mt-1" style={{ color: accent }}>{value}</div>
    </div>
  );
}
